/**
 * CodeDiffPreview — shows a file diff proposal in the Build Agent chat.
 *
 * Displays old (red) vs new (green) content with line numbers.
 * Supports create_file (all green), modify_file (red/green), delete_file (all red).
 * Accept / Reject buttons trigger the proposal apply endpoint.
 */

import { useState } from "react";
import { CheckCircle, XCircle, FileCode, FilePlus, FileX, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface FileDiffProposal {
  proposalId: string;
  type: "file_diff";
  filePath: string;
  description: string;
  summary?: string;
  operationType?: "create_file" | "modify_file" | "delete_file";
  fileOldContent?: string;
  fileContent?: string;
}

interface Props {
  proposal: FileDiffProposal;
  onAccept: (proposalId: string) => Promise<void>;
  onReject: (proposalId: string) => void;
  accepted?: boolean;
  rejected?: boolean;
}

function DiffLine({ line, type }: { line: string; type: "add" | "remove" | "context" }) {
  const bg =
    type === "add" ? "bg-green-500/10 text-green-700 dark:text-green-400" :
    type === "remove" ? "bg-red-500/10 text-red-700 dark:text-red-400" :
    "text-muted-foreground";
  const prefix = type === "add" ? "+" : type === "remove" ? "-" : " ";

  return (
    <div className={`flex gap-2 px-2 py-px font-mono text-xs leading-5 ${bg}`}>
      <span className="select-none w-3 shrink-0 opacity-60">{prefix}</span>
      <span className="whitespace-pre-wrap break-all">{line}</span>
    </div>
  );
}

function FileDiffView({
  oldContent,
  newContent,
  opType,
}: {
  oldContent: string;
  newContent: string;
  opType: "create_file" | "modify_file" | "delete_file";
}) {
  if (opType === "create_file") {
    const lines = newContent.split("\n");
    return (
      <div className="max-h-80 overflow-y-auto rounded border border-border bg-muted/20 text-xs">
        {lines.map((line, i) => <DiffLine key={i} line={line} type="add" />)}
      </div>
    );
  }

  if (opType === "delete_file") {
    const lines = oldContent.split("\n");
    return (
      <div className="max-h-80 overflow-y-auto rounded border border-border bg-muted/20 text-xs">
        {lines.map((line, i) => <DiffLine key={i} line={line} type="remove" />)}
      </div>
    );
  }

  // modify_file: side-by-side removed/added lines
  const oldLines = oldContent.split("\n");
  const newLines = newContent.split("\n");

  // Simple line-by-line diff — highlight all changed lines
  const maxLen = Math.max(oldLines.length, newLines.length);
  const rows: Array<{ old?: string; new?: string; same: boolean }> = [];

  for (let i = 0; i < maxLen; i++) {
    const o = oldLines[i];
    const n = newLines[i];
    if (o === n) {
      rows.push({ old: o, new: n, same: true });
    } else {
      if (o !== undefined) rows.push({ old: o, same: false });
      if (n !== undefined) rows.push({ new: n, same: false });
    }
  }

  return (
    <div className="max-h-80 overflow-y-auto rounded border border-border bg-muted/20 text-xs">
      {rows.map((row, i) =>
        row.same ? (
          <DiffLine key={i} line={row.old!} type="context" />
        ) : row.old !== undefined ? (
          <DiffLine key={`r${i}`} line={row.old} type="remove" />
        ) : (
          <DiffLine key={`a${i}`} line={row.new!} type="add" />
        )
      )}
    </div>
  );
}

export function CodeDiffPreview({ proposal, onAccept, onReject, accepted, rejected }: Props) {
  const [diffOpen, setDiffOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const opType = proposal.operationType ?? "modify_file";

  const Icon =
    opType === "create_file" ? FilePlus :
    opType === "delete_file" ? FileX :
    FileCode;

  const iconColor =
    opType === "create_file" ? "text-green-500" :
    opType === "delete_file" ? "text-destructive" :
    "text-blue-500";

  const label =
    opType === "create_file" ? "Create file" :
    opType === "delete_file" ? "Delete file" :
    "Modify file";

  async function handleAccept() {
    setLoading(true);
    try {
      await onAccept(proposal.proposalId);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="my-2 rounded-lg border border-border bg-card overflow-hidden text-sm">
      {/* Header */}
      <div className="flex items-start justify-between px-3 py-2.5 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`h-4 w-4 shrink-0 ${iconColor}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">{label}</span>
            </div>
            <p className="text-xs font-mono text-foreground truncate">{proposal.filePath}</p>
          </div>
        </div>

        {!accepted && !rejected && (
          <div className="flex items-center gap-1.5 ml-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-destructive/50 text-destructive hover:bg-destructive/10"
              onClick={() => onReject(proposal.proposalId)}
            >
              <XCircle className="h-3 w-3 mr-1" /> Reject
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={handleAccept}
              disabled={loading}
            >
              <CheckCircle className="h-3 w-3 mr-1" /> {loading ? "Applying…" : "Accept"}
            </Button>
          </div>
        )}
        {accepted && (
          <span className="flex items-center gap-1 text-xs text-green-600 ml-2 shrink-0">
            <CheckCircle className="h-3 w-3" /> Applied
          </span>
        )}
        {rejected && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground ml-2 shrink-0">
            <XCircle className="h-3 w-3" /> Rejected
          </span>
        )}
      </div>

      {/* Description */}
      {proposal.description && (
        <p className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border">
          {proposal.description}
        </p>
      )}

      {/* Diff toggle */}
      <button
        className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/20 transition-colors"
        onClick={() => setDiffOpen((v) => !v)}
      >
        <span>View diff</span>
        {diffOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {diffOpen && (
        <div className="px-3 pb-3">
          <FileDiffView
            oldContent={proposal.fileOldContent ?? ""}
            newContent={proposal.fileContent ?? ""}
            opType={opType}
          />
        </div>
      )}
    </div>
  );
}
